// API service for TaskPriority Prime
// Using XMLHttpRequest to avoid conflicts with fetch wrappers
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// XMLHttpRequest-based fetch to avoid rrweb clone issues
const xhrRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    xhr.withCredentials = true;
    
    // Set headers
    const headers = options.headers || {};
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    xhr.onload = () => {
      let data = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        data = { detail: xhr.responseText || 'Unknown error' };
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    
    xhr.onerror = () => {
      reject(new Error('Network error'));
    };
    
    xhr.send(options.body || null);
  });
};

// Safe request wrapper
const safeRequest = async (url, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  return xhrRequest(url, { ...options, headers });
};

// Authenticated request wrapper — relies on httpOnly cookie (set by server)
const authRequest = async (url, options = {}) => {
  const headers = {
    ...options.headers,
  };
  
  // Only set Content-Type for methods that send a body
  if (options.method !== 'DELETE' && options.method !== 'GET') {
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }
  
  const result = await xhrRequest(url, { ...options, headers });
  
  if (result.status === 401) {
    localStorage.removeItem('taskapp_user');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  
  return result;
};

// Auth API
export const authAPI = {
  register: async (username, email, password) => {
    const result = await safeRequest(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      body: JSON.stringify({ username, password, email }),
    });
    
    if (!result.ok) {
      throw new Error(result.data?.detail || 'Registration failed');
    }
    
    if (result.data.user) {
      localStorage.setItem('taskapp_user', JSON.stringify(result.data.user));
    }
    return result.data;
  },
  
  login: async (username, password) => {
    const result = await safeRequest(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (!result.ok) {
      throw new Error(result.data?.detail || 'Login failed');
    }
    
    if (result.data.user) {
      localStorage.setItem('taskapp_user', JSON.stringify(result.data.user));
    }
    return result.data;
  },

  requestPasswordReset: async (email) => {
    const result = await safeRequest(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    
    if (!result.ok) {
      throw new Error(result.data?.detail || 'Failed to send reset email');
    }
    return result.data;
  },

  resetPassword: async (token, new_password) => {
    const result = await safeRequest(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
    });
    
    if (!result.ok) {
      throw new Error(result.data?.detail || 'Password reset failed');
    }
    return result.data;
  },

  processGoogleSession: async (sessionId) => {
    const result = await safeRequest(`${API_BASE_URL}/auth/google/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId,
      },
    });
    
    if (!result.ok) {
      throw new Error(result.data?.detail || 'Failed to process Google session');
    }
    
    if (result.data.user) {
      localStorage.setItem('taskapp_user', JSON.stringify(result.data.user));
    }
    return result.data;
  },
  
  getMe: async () => {
    const result = await authRequest(`${API_BASE_URL}/auth/me`);
    if (!result.ok) throw new Error('Failed to get user');
    return result.data.user;
  },
  
  logout: async () => {
    try {
      await authRequest(`${API_BASE_URL}/auth/logout`, { method: 'POST' });
    } catch (e) {
      console.error('Logout request failed:', e);
    }
    localStorage.removeItem('taskapp_user');
  },
};

// Tasks API
export const tasksAPI = {
  getTasks: async () => {
    // Pass client's local date so server can auto-forward overdue tasks
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    const result = await authRequest(`${API_BASE_URL}/tasks?today=${todayStr}`);
    if (!result.ok) throw new Error('Failed to get tasks');
    return result.data.tasks;
  },
  
  createTask: async (taskData) => {
    const result = await authRequest(`${API_BASE_URL}/tasks`, {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
    
    if (!result.ok) {
      throw new Error(result.data?.detail || 'Failed to create task');
    }
    return result.data.task;
  },
  
  updateTask: async (taskId, updates) => {
    const result = await authRequest(`${API_BASE_URL}/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    
    if (!result.ok) throw new Error('Failed to update task');
    return result.data.task;
  },
  
  deleteTask: async (taskId) => {
    const result = await authRequest(`${API_BASE_URL}/tasks/${taskId}`, {
      method: 'DELETE',
    });
    
    if (!result.ok) throw new Error('Failed to delete task');
    return true;
  },
  
  migrateTasks: async (tasks) => {
    const result = await authRequest(`${API_BASE_URL}/tasks/migrate`, {
      method: 'POST',
      body: JSON.stringify({ tasks }),
    });
    
    if (!result.ok) throw new Error('Failed to migrate tasks');
    return result.data;
  },
};

// Journal API
export const journalAPI = {
  getEntry: async (date) => {
    const result = await authRequest(`${API_BASE_URL}/journal/${date}`);
    if (!result.ok) throw new Error('Failed to get journal entry');
    return result.data.content || '';
  },

  saveEntry: async (date, content) => {
    const result = await authRequest(`${API_BASE_URL}/journal/${date}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
    if (!result.ok) throw new Error('Failed to save journal entry');
    return result.data;
  },

  migrateFromLocal: async (entries) => {
    const result = await authRequest(`${API_BASE_URL}/journal/migrate`, {
      method: 'POST',
      body: JSON.stringify({ entries }),
    });
    if (!result.ok) throw new Error('Failed to migrate journal entries');
    return result.data;
  },

  search: async (query) => {
    const result = await authRequest(`${API_BASE_URL}/journal/search?q=${encodeURIComponent(query)}`);
    if (!result.ok) throw new Error('Failed to search journals');
    return result.data.journals || [];
  },
};
